/**
 * Mobile stepped Customise — the pure step machine (owner 2026-07-27).
 *
 * On phones the Customise pane shows ONE decision at a time so the flow fits
 * the viewport with no page scrolling:
 *
 *   1 Size (+ frame) → 2 Paper (+ C-Type border) → [3 Border, fine art only]
 *
 * The FRAME sits with the size (round 2): picking one rewrites the print size
 * anyway, and once framed the only thing left to choose is the paper. The
 * last step in the list carries "Add to cart" and the quantity stepper, so
 * there is no separate Add step.
 *
 * No React here (same convention as mobileCta.ts / photoOrder.ts) — the whole
 * machine is unit-tested in cfgSteps.test.ts.
 */

import type { BorderMode } from '@rpl/pricing';

export type CfgStep = 'size' | 'paper' | 'border';

/** The steps for the current configuration, in order. */
export function cfgSteps({ framed, fineArt }: { framed: boolean; fineArt: boolean }): CfgStep[] {
  // A frame sets the border itself, so fine art only earns the extra step
  // while it is unframed.
  return fineArt && !framed ? ['size', 'paper', 'border'] : ['size', 'paper'];
}

/**
 * The step actually in play. A key that has fallen out of the list (e.g. the
 * customer picked a frame while sitting on 'border', or a back-swipe restored
 * a step from an older build) clamps to the LAST step rather than throwing
 * them back to the start.
 */
export function cfgResolve(steps: CfgStep[], current: CfgStep): CfgStep {
  return steps.includes(current) ? current : steps[steps.length - 1]!;
}

/** True when this is the final step — the one that adds to the cart. */
export function cfgIsLast(steps: CfgStep[], current: CfgStep): boolean {
  return cfgResolve(steps, current) === steps[steps.length - 1];
}

export function cfgNext(steps: CfgStep[], current: CfgStep): CfgStep | null {
  const i = steps.indexOf(cfgResolve(steps, current));
  return i >= 0 && i < steps.length - 1 ? steps[i + 1]! : null;
}

export function cfgPrev(steps: CfgStep[], current: CfgStep): CfgStep | null {
  const i = steps.indexOf(cfgResolve(steps, current));
  return i > 0 ? steps[i - 1]! : null;
}

export interface CfgGateCtx {
  step: CfgStep;
  /** Final step? Then the gate is the pane's own Add rule. */
  isLast: boolean;
  /** A size must be chosen before anything else can be decided. */
  hasSize: boolean;
  /** Fine-art border choice; null = undecided (blocks the border step). */
  border: BorderMode | null;
  /** The Add rule (photos selected + a size). */
  canAdd: boolean;
}

/**
 * Whether the step's primary action may fire. Gating the fine-art border step
 * on an explicit choice is what keeps BorderGateModal from ever firing for a
 * print added on a phone.
 */
export function cfgCanAdvance({ step, isLast, hasSize, border, canAdd }: CfgGateCtx): boolean {
  if (step === 'border' && border === null) return false;
  if (step === 'size' && !hasSize) return false;
  return isLast ? canAdd : true;
}

/** Primary-action label for the current step (the mobile footer CTA). */
export function cfgCtaLabel({
  step,
  isLast,
  border,
  addLabel,
  total,
}: {
  step: CfgStep;
  isLast: boolean;
  border: BorderMode | null;
  /** The pane's own Add wording, e.g. `Add to cart · 6 prints`. */
  addLabel: string;
  /** Live order-so-far total, e.g. `$1.40` — rides on every Next (owner
   *  2026-08-16: on mobile the price was invisible until the last step). */
  total?: string;
}): string {
  if (step === 'border' && border === null) return 'Choose a border';
  if (isLast) return addLabel;
  return total ? `Next · ${total} →` : 'Next →';
}

/** Human title for the step header ("Step 2 of 2 · Finish"). */
export function cfgStepTitle(step: CfgStep, fineArt: boolean): string {
  if (step === 'size') return 'Size & frame';
  if (step === 'border') return 'Border';
  return fineArt ? 'Paper' : 'Finish';
}

export type CfgSegState = 'done' | 'current' | 'todo';

export interface CfgSegment {
  step: CfgStep;
  /** Step name, e.g. "Size & frame". */
  title: string;
  /** What is chosen for that step, e.g. `6×9"` or "Ilford Pearl". */
  value: string;
  state: CfgSegState;
  /**
   * Only a step already COMPLETED can be tapped. Owner 2026-07-27: the old
   * summary chips were all tappable, and customers used them to hop around
   * instead of working through the flow, landing on steps whose earlier
   * choices were still unmade. Forward movement is the CTA's job now.
   */
  canJump: boolean;
}

/**
 * The progress bar under the preview: one segment per step, showing where you
 * are, what you have chosen so far, and what is still ahead.
 */
export function cfgProgress({
  steps,
  current,
  fineArt,
  sizeLabel,
  paperLabel,
  borderLabel,
}: {
  steps: CfgStep[];
  current: CfgStep;
  fineArt: boolean;
  /** Size, or the frame when one is picked (a frame sets the print size). */
  sizeLabel: string;
  paperLabel: string;
  borderLabel: string;
}): CfgSegment[] {
  const here = steps.indexOf(cfgResolve(steps, current));
  return steps.map((step, i) => {
    const state: CfgSegState = i < here ? 'done' : i === here ? 'current' : 'todo';
    return {
      step,
      title: cfgStepTitle(step, fineArt),
      value: step === 'size' ? sizeLabel : step === 'paper' ? paperLabel : borderLabel,
      state,
      canJump: state === 'done',
    };
  });
}
